import { Card, CardContent, Box, Typography } from "@mui/material";
import { ReactNode } from "react";
import InfoPopper from "../popper/InfoPopper";
import { FeInfoCircleIcon } from "./FeIcons";

interface Props {
  title: string;
  /** optional info tooltip shown as an ⓘ icon next to the title */
  info?: string;
  /**
   * Use Figma's 16px grey vuesax info-circle (borrower-detail charts,
   * 7475:15391) instead of the default 12px glyph.
   */
  largeInfo?: boolean;
  /** optional right-aligned controls (e.g. Absolute / Intensity toggle) */
  action?: ReactNode;
  /** optional footnote / insight rendered under the chart, above a divider */
  footer?: ReactNode;
  /**
   * Header size — "sm" (Figma Section_Header: 14 semibold) for the small
   * chart sections, "lg" (Chart/Component/Heading: 16 medium) for the big
   * bar-chart sections, "h3" (Head/H3_Hreadline_SemiBold: 16 semibold) for the
   * borrower-detail charts.
   */
  size?: "sm" | "lg" | "h3";
  /** Figma borrower charts outline the card instead of relying on elevation. */
  bordered?: boolean;
  /** Space between the header and the chart body (Figma card column gap). */
  gap?: string;
  /**
   * Let the body grow into the card. Figma sizes both cards in a row to the
   * same height, so the shorter chart has to stretch rather than leave a void
   * under its legend.
   */
  fill?: boolean;
  children: ReactNode;
}

/** Figma title styles, keyed by `size`. */
const TITLE_STYLE = {
  sm: { fontSize: "14px", fontWeight: 600, lineHeight: "21px" },
  lg: { fontSize: "16px", fontWeight: 500, lineHeight: "24px" },
  h3: { fontSize: "16px", fontWeight: 600, lineHeight: "24px" },
} as const;

/** Chart section card — Figma "Section" (e.g. node 4173:344). */
const ChartCard = ({
  title,
  info,
  largeInfo = false,
  action,
  footer,
  size = "sm",
  bordered = false,
  gap = "16px",
  fill = false,
  children,
}: Props) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: "8px",
      ...(bordered && { border: "1px solid var(--gray2)", boxSizing: "border-box" }),
      ...(fill && { display: "flex", flexDirection: "column" }),
    }}
  >
    <CardContent
      sx={{
        // Figma strokes sit inside the box, so a bordered card's padding gives
        // up that pixel and the card keeps its Figma height (e.g. 344px).
        p: bordered ? "19px" : "20px",
        "&:last-child": { pb: bordered ? "19px" : "20px" },
        ...(fill && {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        mb={gap}
      >
        <Box display="flex" alignItems="center" gap="8px">
          <Typography {...TITLE_STYLE[size]} color="var(--dark)">
            {title}
          </Typography>
          {info &&
            (largeInfo ? (
              <InfoPopper
                text={info}
                icon={FeInfoCircleIcon}
                iconSize={16}
                iconColor="var(--gray1)"
              />
            ) : (
              // Figma Section_Header info-circle: the same vuesax glyph at 12px, #818181
              <InfoPopper
                text={info}
                icon={FeInfoCircleIcon}
                iconSize={12}
                iconColor="var(--gray1)"
              />
            ))}
        </Box>
        {action}
      </Box>
      {fill ? (
        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
          {children}
        </Box>
      ) : (
        children
      )}
      {footer && (
        <Box
          mt="16px"
          pt="16px"
          sx={{ borderTop: "1px solid var(--gray6)" }}
          fontSize="12px"
          color="var(--gray1)"
        >
          {footer}
        </Box>
      )}
    </CardContent>
  </Card>
);

export default ChartCard;
